/**
 * searchQuery - transaction search query parsing + matching + highlighting
 *
 * Query syntax (kept deliberately simple):
 * - Whitespace between words = AND: every word must match somewhere in the
 *   transaction (description or tags). "uber eats" matches "UBER* EATS PENDING".
 * - Comma between parts = OR: "btc, sol" matches transactions containing
 *   either "btc" or "sol" (each comma part is its own AND group).
 * - Matching is substring-based and NFKC-normalized via searchKey() so
 *   Japanese full-width/half-width forms match each other (F5 Phase 1).
 *
 * A single word behaves exactly like the pre-2026-07 single-substring search.
 * Multi-word queries are a superset of the old contiguous-phrase behavior:
 * any old phrase match still matches under AND semantics.
 *
 * Lives outside TransactionsPage/transactions.ts so it stays unit-testable
 * and respects the 31KB file cap (both consumers are already over it).
 */

import { searchKey } from '../utils/format';

// Guard rails against pathological input (paste of a huge string, etc.)
const MAX_GROUPS = 8;
const MAX_TERMS_PER_GROUP = 8;

export interface ParsedSearchQuery {
  /** OR over groups; AND over normalized terms within a group. */
  groups: string[][];
  /** Flat unique RAW terms (original casing, pre-NFKC) - for highlighting. */
  rawTerms: string[];
}

/**
 * Parse a raw search string into OR-groups of AND-terms.
 * Empty/blank input yields zero groups (= match everything).
 */
export function parseSearchQuery(raw: string): ParsedSearchQuery {
  const groups: string[][] = [];
  const rawTerms: string[] = [];
  const seenRaw = new Set<string>();

  for (const part of raw.split(',').slice(0, MAX_GROUPS)) {
    const words = part.trim().split(/\s+/).filter(Boolean).slice(0, MAX_TERMS_PER_GROUP);
    if (words.length === 0) continue;

    groups.push(words.map((w) => searchKey(w)));

    for (const w of words) {
      const dedupeKey = searchKey(w);
      if (!seenRaw.has(dedupeKey)) {
        seenRaw.add(dedupeKey);
        rawTerms.push(w);
      }
    }
  }

  return { groups, rawTerms };
}

/**
 * Test a transaction's searchable text against a parsed query.
 * A group matches when EVERY term appears in the combined haystack
 * (description + tags, space-joined - terms never contain whitespace, so
 * the join can't create false cross-boundary matches). Any group matching
 * is a hit (OR).
 */
export function matchesSearchQuery(
  parsed: ParsedSearchQuery,
  description: string | undefined,
  tags: string[] | undefined
): boolean {
  if (parsed.groups.length === 0) return true;

  const haystack = [description || '', ...(tags || [])]
    .map((s) => searchKey(s))
    .join(' ');

  return parsed.groups.some((group) => group.every((term) => haystack.includes(term)));
}

// ============================================================
// Highlighting
// ============================================================

export interface HighlightSegment {
  text: string;
  match: boolean;
}

/**
 * Split a display label into matched/unmatched segments for the given raw
 * terms (case-insensitive; overlapping matches are merged). This is a
 * best-effort visual aid on the ORIGINAL string - NFKC width-variant matches
 * (which the filter itself does catch) simply don't highlight, which is
 * harmless. Returns a single unmatched segment when nothing matches.
 */
export function splitHighlight(label: string, terms: string[]): HighlightSegment[] {
  if (!label || terms.length === 0) {
    return [{ text: label, match: false }];
  }

  const lower = label.toLowerCase();
  const ranges: Array<[number, number]> = [];

  for (const term of terms) {
    const needle = term.toLowerCase();
    if (!needle) continue;
    let idx = lower.indexOf(needle);
    while (idx !== -1) {
      ranges.push([idx, idx + needle.length]);
      idx = lower.indexOf(needle, idx + 1);
    }
  }

  if (ranges.length === 0) {
    return [{ text: label, match: false }];
  }

  // Merge overlapping/adjacent ranges
  ranges.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [ranges[0]];
  for (let i = 1; i < ranges.length; i++) {
    const last = merged[merged.length - 1];
    if (ranges[i][0] <= last[1]) {
      last[1] = Math.max(last[1], ranges[i][1]);
    } else {
      merged.push(ranges[i]);
    }
  }

  const segments: HighlightSegment[] = [];
  let cursor = 0;
  for (const [start, end] of merged) {
    if (start > cursor) {
      segments.push({ text: label.slice(cursor, start), match: false });
    }
    segments.push({ text: label.slice(start, end), match: true });
    cursor = end;
  }
  if (cursor < label.length) {
    segments.push({ text: label.slice(cursor), match: false });
  }

  return segments;
}
